import {
  AccessContext,
  DateTimeField,
  DynamicColumn,
  EnumField,
  ForeignKeyField
} from "lego-gui-lib";
import { Required, Schema, TextField } from "lego-gui-lib/lib";
import { api, mainStore } from "../../core/mainStore";
import { CustomerFK } from "../customer/schema";
import { t } from "../../core/i18n/i18n";
import { SANFK } from "../san/schema";
import { RemoteChildDataSource } from "lego-gui-lib/lib/dataAccess/RemoteChildDataSource";
import {
  queryParamDtoStrategy,
  queryParamParentChangeStrategy
} from "lego-gui-lib/lib/dataAccess/ChildDataSourceStrategies";
import { Auth } from "../../core/model/auth";
import { ReportDefinitionFK } from "../reportDefinition/schema";
import { HuntingGroupRequiredFK } from "../huntingGroup/schema";
import { DestinationFK } from "../destination/schema";
import { FormState } from "final-form";
import { EmailOrEmpty } from "../../common/common";

export enum DimensionEnum {
  SAN = 0,
  DESTINATION = 1,
  HUNTING_GROUP = 2
}

export const DimensionTypes = [
  { label: t("hns:san:domain"), value: DimensionEnum.SAN },
  { label: t("hns:destination:domain"), value: DimensionEnum.DESTINATION },
  { label: t("hns:huntingGroup:domain"), value: DimensionEnum.HUNTING_GROUP }
];

export const ReportSubscriptionSchema = new Schema<DynamicColumn>({
  primaryKey: ["id"],
  security: {
    readableRole: Auth.Roles.ANYONE,
    editableRole: Auth.Roles.ANYONE_WITH_WRITE
  },
  fields: [
    {
      name: "customerRef",
      type: CustomerFK,
      sort: { field: "name" },
      title: t("hns:destination:type.customerRef")
    },
    {
      name: "name",
      type: TextField(Required),
      title: t("common:name"),
      width: "300px"
    },
    {
      name: "description",
      type: TextField(),
      title: t("common:description"),
      width: "300px"
    },
    {
      name: "dimension",
      type: EnumField(DimensionTypes, Required),
      title: t("hns:reportSubscription:dimension")
    },
    {
      name: "sanRef",
      type: SANFK,
      sort: { field: "name" },
      title: t("hns:destination:type.sanRef")
    },
    {
      name: "huntingGroupRef",
      title: t("hns:huntingGroup:domain"),
      type: HuntingGroupRequiredFK
    },
    {
      name: "destinationRef",
      type: DestinationFK,
      title: t("hns:reportSubscription:destinationRef")
    },
    {
      name: "recipientEmail",
      type: TextField(EmailOrEmpty),
      title: t("hns:reportSubscription:recipientEmail")
    },
    {
      name: "reportDefinitionRef",
      type: ReportDefinitionFK,
      title: t("hns:reportSubscription:reportDefinitionRef"),
      width: "300px"
    }
    // {
    //   name: "lastReportDate",
    //   type: DateTimeField(),
    //   title: t("hns:reportSubscription:lastReportDate"),
    //   width: "200px"
    // }
  ]
});

const onDimensionChange = (
  newValue: any,
  oldValue: any,
  formState: FormState<any>
) => {
  if (newValue === DimensionEnum.SAN) {
    formState.values["sanRef"] = mainStore.SAN.oneDataSource.one.id;
  } else {
    formState.values["huntingGroupRef"] = undefined;
    formState.values["destinationRef"] = undefined;
    formState.values["sanRef"] = undefined;
  }
};

export const ReportSubscriptionSchemaForm = ReportSubscriptionSchema.extend<
  DynamicColumn
>({
  dimension: {
    type: EnumField(DimensionTypes, Required),
    onChange: onDimensionChange
  },
  huntingGroupRef: {
    if: (ctx: AccessContext) =>
      ctx.data?.dimension === DimensionEnum.HUNTING_GROUP,
    type: HuntingGroupRequiredFK,
    title: t("hns:huntingGroup:domain")
  },
  destinationRef: {
    if: (ctx: AccessContext) =>
      ctx.data?.dimension === DimensionEnum.DESTINATION,
    type: DestinationFK,
    title: t("hns:reportSubscription:destinationRef")
  },
  customerRef: false,
  sanRef: {
    if: (ctx: AccessContext) => ctx.data?.dimension === DimensionEnum.SAN,
    type: SANFK,
    title: t("hns:reportSubscription:sanRef"),
    readonly: true
  },
  lastReportDate: false
});

export const ReportSubscriptionSchemaColumns = new Schema<DynamicColumn>({
  primaryKey: ["id"],
  security: {
    readableRole: Auth.Roles.ANYONE,
    editableRole: Auth.Roles.ANYONE_WITH_WRITE
  },
  fields: [
    {
      name: "name",
      type: TextField(Required),
      title: t("common:name"),
      width: "15%"
    },
    {
      name: "description",
      type: TextField(),
      title: t("common:description"),
      width: "15%"
    },
    {
      name: "dimension",
      type: EnumField(DimensionTypes, Required),
      title: t("hns:reportSubscription:dimension"),
      width: "15%"
    },
    {
      name: "dimensionParam",
      title: t("hns:reportSubscription:dimensionParam"),
      // this column will match when generating table headers
      type: TextField(), // does not matter, it is just a placeholder as `type` does not allow null value
      if: (ctx: AccessContext) => ctx.data?.dimension === undefined,
      filter: false,
      sort: false,
      width: "15%"
    },
    {
      ...ReportSubscriptionSchemaForm.Named["sanRef"]
    },
    {
      ...ReportSubscriptionSchemaForm.Named["huntingGroupRef"]
    },
    {
      ...ReportSubscriptionSchemaForm.Named["destinationRef"]
    },
    {
      name: "reportDefinitionRef",
      type: ReportDefinitionFK,
      title: t("hns:reportSubscription:reportDefinitionRef"),
      width: "15%"
    }
    // {
    //   name: "lastReportDate",
    //   type: DateTimeField(),
    //   title: t("hns:reportSubscription:lastReportDate"),
    //   width: "15%"
    // }
  ]
});

mainStore.reportSubscription = RemoteChildDataSource.create({
  parent: mainStore.SAN.oneDataSource,
  parentChangedStrategy: queryParamParentChangeStrategy("id", "sanRef"),
  dtoStrategy: queryParamDtoStrategy("id", "sanRef"),
  api: api,
  name: "reportSubscription",
  schema: ReportSubscriptionSchema,
  url: "/reportSubscriptions",
  commons: mainStore.commons
});

mainStore.reportSubscriptionFk = RemoteChildDataSource.clone(
  mainStore.destination
);

export const ReportSubscriptionFK = ForeignKeyField(
  {
    dataSource: mainStore.reportSubscriptionFk.listDataSource,
    labelField: "name",
    valueField: "id",
    minWidth: 70
  },
  Required
);
